import "./qa-premium.css";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { BAND_META, formatDate, useQaPerms } from "./qa-shared";

const RATING_META: Record<string, { label: string; cls: string }> = {
  non_compliant: { label: "Non-Compliant", cls: "rtg-nc" },
  partially_compliant: { label: "Partially Compliant", cls: "rtg-pc" },
};

const generatePDFHTML = (data: any) => {
  const { visit, report, domainScores, findings } = data;
  const score = report ? Number(report.overallScore) : 0;
  const band = report ? BAND_META[report.interpretationBand] : null;

  // Visit Info
  const visitReference = visit.visitNumber || "";
  const dataCentre = visit.siteName ?? `Site #${visit.siteId}`;
  const visitDate = formatDate(visit.visitDate);
  const auditor = data.auditorName ?? "—";
  const approvedBy = data.approvedByName ?? "—";

  // Findings HTML
  const findingsHTML = findings.map((f: any, idx: number) => {
    const riskClass = f.riskLevel === "high" ? "high-risk" : "medium-risk";
    const riskText = f.rating === "partially_compliant" ? "Partially Compliant" : "Non-Compliant";
    const riskAr = f.rating === "partially_compliant" ? "متوافق جزئياً" : "غير متوافق";
    const riskLevelLabel = f.riskLevel === "high" ? "High" : "Medium";
    const riskLevelAr = f.riskLevel === "high" ? "عالية" : "متوسطة";

    return `
      <div class="finding ${riskClass}">
        <div class="bilingual-row">
          <div class="en-part">
            <strong>${f.questionNumber} ${f.text}</strong><br>
            <span style="color:${f.riskLevel === "high" ? "#C53030" : "#E08B00"}; font-weight:600;">${riskText} • ${riskLevelLabel} Risk</span>
          </div>
          <div class="ar-part">
            <strong>${f.questionNumber} ${f.text || ""}</strong><br>
            <span style="color:${f.riskLevel === "high" ? "#C53030" : "#E08B00"}; font-weight:600;">${riskAr} • مخاطر ${riskLevelAr}</span>
          </div>
        </div>
        <p style="margin-top:12px;">
          <strong>Corrective Action:</strong> ${f.correctiveAction || "—"} &nbsp;&nbsp; | &nbsp;&nbsp;
          <strong>Target:</strong> ${f.targetCompletionDate ? formatDate(f.targetCompletionDate) : "—"}
        </p>
      </div>
    `;
  }).join("");

  // Domain Scores HTML
  const domainScoresHTML = domainScores.map((d: any) => {
    const ds = d.domainScore == null ? null : Number(d.domainScore);
    const scoreDisplay = ds == null ? "N/A" : `${ds.toFixed(1)}%`;
    return `
      <tr>
        <td>${d.domainName}</td>
        <td>${(d.weightBps / 100).toFixed(0)}%</td>
        <td style="color:${ds && ds >= 80 ? "#166534" : "#E08B00"};">${scoreDisplay}</td>
        <td>${Number(d.weightedContribution).toFixed(1)}</td>
        <td style="text-align:right; direction:rtl;">${d.domainName}</td>
      </tr>
    `;
  }).join("");

  // Corrective Action Plan HTML
  const correctiveActionHTML = findings.map((f: any, i: number) => `
    <tr>
      <td>${i+1}</td>
      <td>${f.text}</td>
      <td style="color:${f.riskLevel === "high" ? "#C53030" : "#E08B00"}">${f.riskLevel ? f.riskLevel.charAt(0).toUpperCase() + f.riskLevel.slice(1) : "—"}</td>
      <td>${f.targetCompletionDate ? formatDate(f.targetCompletionDate) : "—"}</td>
      <td style="text-align:right; direction:rtl;">${f.text}</td>
    </tr>
  `).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>QA Assessment Report - ${visitReference}</title>
<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap');
  body {
    margin:0; padding:0; background:#F4F4F7;
    font-family:'STC Forward','STC_Forward','Cairo',Arial,sans-serif;
    color:#1D252D; line-height:1.6;
  }
  .download-bar {
    position: sticky; top: 0; background: #fff; padding: 15px 45px;
    border-bottom: 1px solid #E5E7EB; display: flex; justify-content: flex-end;
    gap: 10px; z-index: 100; box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .download-btn {
    background: #431076; color: #fff; border: none; padding: 10px 20px;
    border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px;
    font-family: inherit; transition: background 0.2s;
  }
  .download-btn:hover {
    background: #5E1F9E;
  }
  .container {
    max-width: 980px; margin: 30px auto; background:#fff;
    border-radius:16px; overflow:hidden;
    box-shadow:0 15px 50px rgba(67,16,118,0.12);
  }

  .header {
    background: linear-gradient(90deg, #431076 0%, #5E1F9E 100%);
    color:#fff; padding:35px 45px;
  }
  .logo-row { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
  .header h1 { margin:0; font-size:28px; font-weight:700; }
  .header p { margin:8px 0 0; opacity:0.9; font-size:15px; }

  @media print {
    .download-bar { display: none; }
  }

  .content { padding:45px; }

  .section-title {
    color:#431076; font-size:20px; font-weight:600;
    margin:35px 0 18px; border-bottom:2px solid #F0E6FF;
    padding-bottom:8px;
  }

  table { width:100%; border-collapse:collapse; margin:20px 0; }
  th, td { padding:14px 18px; border-bottom:1px solid #E5E7EB; }
  th { background:#F8F9FA; font-weight:600; color:#4A5568; width:28%; }

  .bilingual-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }
  .en-part { flex: 1; }
  .ar-part {
    flex: 1;
    text-align: right;
    direction: rtl;
  }

  .overall-box {
    background:linear-gradient(135deg, #F0FDF4, #ECFDF5);
    border:2px solid #86EFAC; border-radius:16px;
    padding:32px; text-align:center; margin:32px 0;
  }
  .score-circle { font-size:62px; font-weight:700; color:#166534; line-height:1; }
  .badge {
    display:inline-block; background:#166534; color:white;
    padding:8px 24px; border-radius:9999px; font-size:15px; margin-top:12px;
  }

  .finding {
    padding:22px; border-radius:10px; margin:20px 0;
    border-left:6px solid;
  }
  .high-risk { border-color:#C53030; background:#FEF2F2; }
  .medium-risk { border-color:#E08B00; background:#FFFBEB; }

  .footer {
    background:#431076; color:#E0D4FF; padding:28px 45px;
    text-align:center; font-size:13.5px;
  }

  @media print {
    body { background: white; }
    .container { margin: 0; border-radius: 0; box-shadow: none; }
  }
</style>
</head>
<body>

<div class="download-bar">
  <button class="download-btn" onclick="downloadPDF()">📥 Download PDF</button>
</div>

<div class="container" id="pdfContent">

  <!-- HEADER -->
  <div class="header">
    <div class="logo-row">
      <svg width="48" height="48" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2 L34 18 L18 34 L2 18 Z" fill="none" stroke="#ffffff" stroke-width="2.5"/>
        <line x1="18" y1="2" x2="18" y2="34" stroke="#ffffff" stroke-width="2.5"/>
        <line x1="2" y1="18" x2="34" y2="18" stroke="#ffffff" stroke-width="2.5"/>
        <rect x="12" y="12" width="12" height="12" fill="#FF375E"/>
      </svg>
      <div>
        <span style="font-size:28px;font-weight:700;letter-spacing:-1px;">permit<span style="color:#FF375E;">3</span></span>
      </div>
    </div>
    <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:40px;">
      <div>
        <h1 style="margin:0 0 8px 0;">QA Assessment Report</h1>
        <p style="margin:0; opacity:0.9; font-size:15px;">Data Centre Quality Audit Visit</p>
      </div>
      <div style="text-align:right; direction:rtl;">
        <h1 style="margin:0 0 8px 0;">تقرير تقييم الجودة</h1>
        <p style="margin:0; opacity:0.9; font-size:15px;">زيارة تدقيق جودة مركز البيانات</p>
      </div>
    </div>
  </div>

  <div class="content">

    <!-- Visit Information -->
    <h2 class="section-title">Visit Information • معلومات الزيارة</h2>
    <table>
      <tr>
        <td><strong>${visitReference}</strong></td>
        <th>VISIT REFERENCE</th>
        <th style="text-align:right; direction:rtl;">مرجع الزيارة</th>
      </tr>
      <tr>
        <td><strong>${dataCentre}</strong></td>
        <th>DATA CENTRE</th>
        <th style="text-align:right; direction:rtl;">مركز البيانات</th>
      </tr>
      <tr>
        <td>${visitDate}</td>
        <th>VISIT DATE</th>
        <th style="text-align:right; direction:rtl;">تاريخ الزيارة</th>
      </tr>
      <tr>
        <td>${auditor}</td>
        <th>QAG AUDITOR</th>
        <th style="text-align:right; direction:rtl;">المدقق</th>
      </tr>
      <tr>
        <td>${approvedBy}</td>
        <th>APPROVED BY</th>
        <th style="text-align:right; direction:rtl;">معتمد من</th>
      </tr>
    </table>

    <!-- Overall Result -->
    <div class="overall-box">
      <div style="font-size:17px; color:#166534; font-weight:600;">OVERALL RESULT • النتيجة الإجمالية</div>
      <div class="score-circle">${Math.round(score)}%</div>
      <div class="badge">${band?.label ?? "—"} • ${band?.label ?? "—"}</div>
      <p style="margin-top:18px; color:#14532D; max-width:700px; margin-left:auto; margin-right:auto;">
        Overall weighted compliance across all domains.<br>
        الامتثال المرجح الإجمالي عبر جميع المجالات.
      </p>
    </div>

    <!-- Domain Scores -->
    <h2 class="section-title">Domain Scores • درجات المجالات</h2>
    <table>
      <thead>
        <tr>
          <th>DOMAIN</th>
          <th>WEIGHT</th>
          <th>SCORE</th>
          <th>CONTRIBUTION</th>
          <th style="text-align:right; direction:rtl;">المجال</th>
        </tr>
      </thead>
      <tbody>
        ${domainScoresHTML}
      </tbody>
    </table>

    <!-- Findings -->
    <h2 class="section-title">Findings & Non-Compliances • النتائج وعدم الامتثال</h2>
    ${findings.length === 0
      ? `<p style="background: #F0FDF4; padding: 15px; border-radius: 8px; color: #166534;">No non-compliances recorded — all rated requirements were met.</p>`
      : findingsHTML}

    <!-- Corrective Action Plan -->
    ${findings.length > 0 ? `
    <h2 class="section-title">Corrective Action Plan • خطة الإجراءات التصحيحية</h2>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>FINDING</th>
          <th>RISK</th>
          <th>TARGET DATE</th>
          <th style="text-align:right; direction:rtl;">الإجراء</th>
        </tr>
      </thead>
      <tbody>
        ${correctiveActionHTML}
      </tbody>
    </table>` : ""}

  </div>

  <!-- FOOTER -->
  <div class="footer">
    <p><strong>permit3</strong> • Governance Management System • نظام إدارة الحوكمة</p>
    <p style="margin-top:8px; opacity:0.85;">
      Confidential Document • وثيقة سرية • ${visitReference}
    </p>
  </div>

</div>

<script>
function downloadPDF() {
  var printWindow = window.open('', '', 'width=1200,height=800');
  var element = document.getElementById('pdfContent');

  printWindow.document.write('<!DOCTYPE html><html><head>');
  printWindow.document.write('<meta charset="UTF-8">');
  printWindow.document.write('<meta name="viewport" content="width=device-width, initial-scale=1.0">');
  printWindow.document.write('<title>QA-Assessment-Report-${visitReference}</title>');
  printWindow.document.write('</head><body>');
  printWindow.document.write(element.innerHTML);
  printWindow.document.write('</body></html>');
  printWindow.document.close();

  setTimeout(function() {
    printWindow.print();
  }, 250);
}
</script>

</body>
</html>`;
};

export default function QaReport() {
  const [, params] = useRoute("/qa/visits/:id/report");
  const [, setLocation] = useLocation();
  const visitId = params?.id ? Number(params.id) : undefined;
  const perms = useQaPerms();

  const { data, isLoading, refetch } = trpc.qa.reports.getFull.useQuery({ visitId: visitId! }, { enabled: !!visitId });
  const generate = trpc.qa.reports.generate.useMutation({ onSuccess: () => { toast.success("Report generated"); refetch(); }, onError: (e) => toast.error(e.message) });
  const approve = trpc.qa.reports.approve.useMutation({ onSuccess: () => { toast.success("Report approved"); refetch(); }, onError: (e) => toast.error(e.message) });
  const reject = trpc.qa.reports.reject.useMutation({ onSuccess: () => { toast.success("Sent back for rework"); setLocation(`/qa/visits/${visitId}`); }, onError: (e) => toast.error(e.message) });

  if (isLoading) return <div className="qa-premium"><div className="wrap"><div className="loading"><span className="spin" /></div></div></div>;
  if (!data) return <div className="qa-premium"><div className="wrap" style={{ paddingTop: 24 }}><div className="empty">Visit not found.</div></div></div>;

  const { visit, report, domainScores, findings } = data;
  const band = report ? BAND_META[report.interpretationBand] : null;
  const approved = report?.status === "approved";
  const canGenerate = ["assessment_submitted", "report_approved"].includes(visit.status);
  const score = report ? Number(report.overallScore) : 0;

  return (
    <div className="qa-premium full">
      <div className="rtoolbar qa-no-print">
        <span className="t"><i className="ti ti-file-text" /> Report preview</span>
        <button onClick={() => setLocation(`/qa/visits/${visitId}`)} className="btn btn-ghost"><i className="ti ti-arrow-left" /> Back</button>
        {report && !approved && perms.reportView && (
          <button onClick={() => generate.mutate({ visitId: visitId! })} disabled={generate.isPending} className="btn btn-ghost"><i className="ti ti-refresh" /> Regenerate</button>
        )}
        {report && !approved && perms.reportApprove && (
          <>
            <button onClick={() => reject.mutate({ visitId: visitId! })} className="btn btn-ghost" style={{ color: "var(--red)" }}><i className="ti ti-arrow-back-up" /> Send back</button>
            <button onClick={() => approve.mutate({ visitId: visitId! })} className="btn btn-primary" style={{ background: "var(--green)" }}><i className="ti ti-circle-check" /> Approve</button>
          </>
        )}
        {report && (
          <button onClick={() => {
            const htmlContent = generatePDFHTML(data);
            const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `QA-Assessment-Report-${visit.visitNumber}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Report downloaded');
          }} className="btn btn-primary"><i className="ti ti-download" /> Download PDF</button>
        )}
      </div>

      {!report ? (
        <div className="wrap">
          <div className="empty">
            <i className="ti ti-file-text" />
            <div style={{ marginBottom: 14 }}>No report generated yet.</div>
            {canGenerate && perms.reportView && (
              <button onClick={() => generate.mutate({ visitId: visitId! })} disabled={generate.isPending} className="btn btn-primary" style={{ display: "inline-flex" }}><i className="ti ti-file-text" /> Generate Report</button>
            )}
          </div>
        </div>
      ) : (
        <div className="qa-report-doc">
          {/* HEADER */}
          <div className="rhead">
            {approved
              ? <span className="rapproved"><i className="ti ti-circle-check" style={{ fontSize: 12 }} /> APPROVED</span>
              : <span className="rdraft">DRAFT</span>}
            <div className="rbrand"><span className="lg"><i className="ti ti-building-skyscraper" /></span> centre3 · Governance Management System</div>
            <h1>QA Assessment Report</h1>
            <div className="rsub">Data Centre Quality Audit Visit</div>
          </div>

          <div className="rpad">
            {/* META */}
            <div className="rmeta">
              <div className="m"><div className="k">Visit Reference</div><div className="val" style={{ fontFamily: "ui-monospace, monospace", color: "var(--p)" }}>{visit.visitNumber}</div></div>
              <div className="m"><div className="k">Data Centre</div><div className="val">{visit.siteName ?? `Site #${visit.siteId}`}</div></div>
              <div className="m"><div className="k">Visit Date</div><div className="val">{formatDate(visit.visitDate)}</div></div>
              <div className="m"><div className="k">Template</div><div className="val">{data.templateName ?? "—"} · v{data.versionNumber ?? "—"}</div></div>
              <div className="m"><div className="k">QAG Auditor</div><div className="val">{data.auditorName ?? "—"}</div></div>
              <div className="m"><div className="k">Approved By</div><div className="val">{data.approvedByName ?? "—"}</div></div>
            </div>

            {/* OVERALL */}
            <div className="rst"><i className="ti ti-gauge" /> Overall Result</div>
            <div className="scorebox">
              <div className="gauge" style={{ background: `conic-gradient(${band?.color ?? "var(--p)"} 0 ${score}%, #E3DCEE ${score}% 100%)` }}>
                <div className="gin"><b style={{ color: band?.color ?? "var(--p)" }}>{Math.round(score)}%</b><span>Weighted</span></div>
              </div>
              <div>
                <div className="band" style={{ color: band?.color ?? "var(--p)" }}>{band?.label ?? "—"}</div>
                <div className="desc">Overall weighted compliance across all domains. See per-domain breakdown and findings below.</div>
              </div>
            </div>

            {/* DOMAIN SCORES */}
            <div className="rst"><i className="ti ti-chart-pie" /> Domain Scores</div>
            <div className="rtbl">
              <table>
                <thead><tr><th>Domain</th><th className="center">Weight</th><th className="center">Rated / N/A</th><th className="center">Score</th><th className="right">Contribution</th></tr></thead>
                <tbody>
                  {domainScores.map((d: any) => {
                    const ds = d.domainScore == null ? null : Number(d.domainScore);
                    const color = ds == null ? "var(--text3)" : ds >= 80 ? "var(--green)" : ds >= 70 ? "var(--amber)" : "var(--red)";
                    return (
                      <tr key={d.id}>
                        <td>{d.domainName}</td>
                        <td className="center">{(d.weightBps / 100).toFixed(0)}%</td>
                        <td className="center">{d.ratedCount} / {d.naCount}</td>
                        <td className="center">
                          {ds != null && <span className="dbar"><span style={{ width: `${ds}%` }} /></span>}{" "}
                          <span style={{ color, fontWeight: 800 }}>{ds == null ? "N/A" : `${ds.toFixed(1)}%`}</span>
                        </td>
                        <td className="right">{Number(d.weightedContribution).toFixed(1)}</td>
                      </tr>
                    );
                  })}
                  <tr className="totalrow">
                    <td>Overall</td><td className="center">100%</td>
                    <td className="center">{domainScores.reduce((a: number, d: any) => a + d.ratedCount, 0)} / {domainScores.reduce((a: number, d: any) => a + d.naCount, 0)}</td>
                    <td className="center">{Math.round(score)}%</td>
                    <td className="right">{score.toFixed(1)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>Compliant = 1.0 · Partially Compliant = 0.8 · Non-Compliant = 0 · N/A excluded (weights renormalised). Bands: ≥90 Excellent · 80–89 Strong · 70–79 Moderate · &lt;70 High Risk.</div>

            {/* FINDINGS */}
            <div className="rst"><i className="ti ti-alert-triangle" /> Findings &amp; Non-Compliances <span className="badge">{findings.length} item{findings.length === 1 ? "" : "s"}</span></div>
            {findings.length === 0 ? (
              <div className="exec">No non-compliances recorded — all rated requirements were met.</div>
            ) : (
              findings.map((f: any, i: number) => {
                const rm = RATING_META[f.rating] ?? { label: f.rating, cls: "rtg-nc" };
                const riskCls = f.riskLevel === "high" ? "r-high" : f.riskLevel === "medium" ? "r-medium" : "r-low";
                return (
                  <div key={i} className={`finding ${f.rating === "partially_compliant" ? "pc" : ""}`}>
                    <div className="fh">
                      <span className="qn">{f.questionNumber}</span>
                      <span className="ft">{f.text}</span>
                      <span className={`rtg ${rm.cls}`}>{rm.label}</span>
                      {f.riskLevel && <span className={`risk ${riskCls}`}>{f.riskLevel} risk</span>}
                    </div>
                    <div className="fb">
                      <div><span className="k">Evidence reviewed</span><br />{f.evidenceReviewed || "—"}</div>
                      <div><span className="k">Corrective action</span><br />{f.correctiveAction || "—"}</div>
                      <div><span className="k">Responsible dept.</span><br />{f.deptName || "—"}</div>
                      <div><span className="k">Target completion</span><br />{f.targetCompletionDate ? formatDate(f.targetCompletionDate) : "—"}</div>
                    </div>
                  </div>
                );
              })
            )}

            {/* CAPA */}
            {findings.length > 0 && (
              <>
                <div className="rst"><i className="ti ti-list-check" /> Corrective Action Plan</div>
                <div className="rtbl">
                  <table>
                    <thead><tr><th>#</th><th>Finding</th><th>Risk</th><th>Owner</th><th>Target</th></tr></thead>
                    <tbody>
                      {findings.map((f: any, i: number) => {
                        const riskCls = f.riskLevel === "high" ? "r-high" : f.riskLevel === "medium" ? "r-medium" : "r-low";
                        return (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{f.correctiveAction || f.text}</td>
                            <td>{f.riskLevel ? <span className={`risk ${riskCls}`}>{f.riskLevel}</span> : "—"}</td>
                            <td>{f.deptName || "—"}</td>
                            <td>{f.targetCompletionDate ? formatDate(f.targetCompletionDate) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* SIGN-OFF */}
            <div className="rst"><i className="ti ti-writing-sign" /> Approval &amp; Sign-off</div>
            <div className="sign">
              <div className="sbox">
                <div className="srole">Conducted by · QAG Auditor</div>
                <div className="snm">{data.auditorName ?? "—"}</div>
                <div className="sline"><span>On-site assessment</span><span>{formatDate(visit.assessmentSubmittedAt) }</span></div>
              </div>
              <div className="sbox" style={{ borderColor: approved ? "var(--green)" : "var(--border)" }}>
                <div className="srole" style={{ color: approved ? "var(--green)" : "var(--text3)" }}>Approved by · QAG Lead</div>
                <div className="snm">{data.approvedByName ?? "Pending approval"}</div>
                <div className="sline">
                  <span>{approved ? <><i className="ti ti-circle-check" style={{ color: "var(--green)" }} /> Digitally approved</> : "Awaiting approval"}</span>
                  <span>{approved ? formatDate(report.approvedAt) : "—"}</span>
                </div>
              </div>
            </div>

            {/* APPROVAL ACTIONS */}
            {!approved && perms.canReportApprove && (
              <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
                <button
                  onClick={() => approve.mutate({ visitId: visitId! })}
                  disabled={approve.isPending}
                  style={{
                    background: "var(--green)",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {approve.isPending ? <span className="spin" style={{ fontSize: 14 }} /> : <i className="ti ti-circle-check" />}
                  Approve Report
                </button>
                <button
                  onClick={() => {
                    const reason = prompt("Reason for rejection (optional):");
                    if (reason !== null) reject.mutate({ visitId: visitId!, reason });
                  }}
                  disabled={reject.isPending}
                  style={{
                    background: "var(--border)",
                    color: "var(--text1)",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {reject.isPending ? <span className="spin" style={{ fontSize: 14 }} /> : <i className="ti ti-x" />}
                  Reject for Rework
                </button>
              </div>
            )}
          </div>

          <div className="rfoot">
            <span>centre3 · Governance Management System — QA Assessment Report</span>
            <span>{visit.visitNumber} · Confidential</span>
          </div>
        </div>
      )}
      <div style={{ height: 30 }} />
    </div>
  );
}
